<script>
import Panel from '@/components/ui/Panel';
import Selectable from '@/components/selectable/Selectable';
import Promise from 'bluebird';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'RevisionPanel',
  components: {
    Panel,
    Selectable
  },
  props: {
    currentRevision: Object,
    loading: Boolean,
    revisions: Array,
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isDeleting: false,
      selection: []
    };
  },
  methods: {
    show() {
      this.$refs.panel.show();

      // Select the current revision by default
      if (this.currentRevision) {
        this.$refs.selectable.select(this.currentRevision);
      } else {
        this.$refs.selectable.selectNone();
      }
    },

    hide() {
      this.$refs.panel.hide();
    },

    handleSubmit() {
      if (this.selection.length) {
        this.$emit('submit', this.selection[0]);
        this.hide();
      }
    },

    handleDelete() {
      if (this.selection.length) {
        const message = this.$tc('Are you sure you want to delete {count} files?', this.selection.length, {
          count: this.selection.length
        });
        this.$confirm(message, {
          title: this.$t('Delete'),
          confirmButtonText: this.$t('Delete'),
          confirmButtonClass: 'el-button--danger',
          cancelButtonText: this.$t('Cancel'),
          callback: action => {
            if (action === 'confirm') {
              this.isDeleting = true;
              Promise.map(
                this.selection,
                revision => {
                  return this.$http.delete(`/sites/${this.site.id}/pages/${revision.pageId}/revisions/${revision.id}`);
                },
                { concurrency: 10 }
              )
                .then(res => this.$emit('delete', res.map(r => r.data.revision)))
                .catch(err => {
                  this.$notify({
                    type: 'error',
                    title: this.$t('Error'),
                    message: getErrorMessage(err.response.data.code)
                  });
                })
                .finally(() => (this.isDeleting = false));
            }
          }
        });
      }
    }
  }
};
</script>

<template>
  <panel ref="panel" :title="$t('Drafts & Revisions')">
    <selectable
      ref="selectable"
      v-model="selection"
      :items="revisions"
      :multiple="true"
    >
      <div v-loading="loading || isDeleting" class="revisions-container">
        <div v-if="revisions.length" class="revisions">
          <div
            v-for="revision in revisions" :key="revision.id"
            :class="{
              'revisions-item': true,
              'is-selected': $refs.selectable.isSelected(revision)
            }"
            @click="$refs.selectable.select(revision, $event)"
            @dblclick="handleSubmit"
          >
            <div class="avatar">
              <img
                :src="revision.user.avatar ? revision.user.avatar : require('@/assets/user-light.svg')"
                :alt="revision.user.name"
              >
            </div>
            <div class="details">
              <div class="title">
                {{ $d(new Date(revision.createdAt), 'long') }}
              </div>
              <small class="description">
                <span v-if="revision.isDraft">
                  {{ $t('Saved by {name}', { name: revision.user.name }) }}
                </span>
                <span v-else>
                  {{ $t('Published by {name}', { name: revision.user.name }) }}
                </span>
              </small>
            </div>
            <div class="type">
              <!-- Draft -->
              <el-tag v-if="revision.isDraft" type="warning" size="small">
                {{ $t('Draft') }}
              </el-tag>
            </div>
          </div>
        </div>
        <div v-else class="revisions revisions-empty">
          {{ $t('No Revisions') }}
        </div>
      </div>
    </selectable>

    <el-row :gutter="20">
      <el-col :span="12">
        <el-button :disabled="selection.length !== 1" type="primary" @click="handleSubmit">
          {{ $t('Open Revision') }}
        </el-button>
      </el-col>
      <el-col :span="12" class="text-right">
        <el-button :title="$t('Delete')" :disabled="selection.length === 0" @click="handleDelete">
          <i class="el-icon-fa-trash" />
        </el-button>
      </el-col>
    </el-row>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.revisions-container {
  background: $--color-white;
  border: solid 1px $--color-gray-20;
  border-radius: 0.25rem;
  line-height: 1.4;
  user-select: none;
  margin-bottom: 1.5rem;
}

.revisions {
  min-height: 18.75rem; // ~ 5 items
  max-height: calc(100vh - 200px);
  overflow: auto;
  padding: 0;

  &.revisions-empty {
    display: flex;
    color: $--color-gray-40;
    align-items: center;
    justify-content: center;
    color: $--color-gray-40;
  }

  @include breakpoint('sm-and-down') {
    max-height: calc(100vh - 150px);
  }
}

.revisions-item {
  display: flex;
  padding: 0.5rem 1rem;
  align-items: center;
  transition: 0.1s background-color;

  .avatar {
    flex: 0 0 auto;

    img {
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      vertical-align: middle;
    }
  }

  .details {
    flex: 1 1 auto;
    padding: 0 1rem;

    .title {
      transition: 0.1s color;
    }

    .description {
      color: $--color-gray-40;
      transition: 0.1s color;
    }
  }

  .type {
    flex: 0 0 auto;
  }

  &.is-selected {
    background-color: $--color-primary-light-9;

    .name,
    .date {
      color: $--color-primary;
    }
  }
}
</style>
